import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("dms_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ---- Refresh token rotation ----
let refreshInFlight = null;

async function performRefresh() {
  const refresh_token = localStorage.getItem("dms_refresh");
  if (!refresh_token) throw new Error("no_refresh");
  // Use a bare axios call to avoid recursion via the configured `api` interceptor
  const { data } = await axios.post(`${API}/auth/refresh`, { refresh_token });
  localStorage.setItem("dms_token", data.access_token);
  if (data.refresh_token) localStorage.setItem("dms_refresh", data.refresh_token);
  return data.access_token;
}

// Sprint CONCORRENZA — sessione scaduta: niente salto alla pagina di accesso. Le richieste
// restano in attesa, la finestra SessioneScaduta chiede la password e, se rientra lo STESSO
// utente, le richieste ripartono con il nuovo accesso; i dati a schermo restano.
let rientro = null;

function attendiRientro(motivo) {
  if (!rientro) {
    let resolve, reject;
    const promise = new Promise((res, rej) => { resolve = res; reject = rej; });
    rientro = { promise, resolve, reject };
    let email = "";
    try { email = JSON.parse(localStorage.getItem("dms_user") || "null")?.email || ""; } catch { /* vuoto */ }
    window.dispatchEvent(new CustomEvent("kargest:sessione-scaduta", { detail: { motivo, email } }));
  }
  return rientro.promise;
}

export function completaRientro(token) {
  if (rientro) { rientro.resolve(token); rientro = null; }
}

export function annullaRientro() {
  if (rientro) { rientro.reject(new Error("rientro_annullato")); rientro = null; }
}

function forceLogout(reason) {
  localStorage.removeItem("dms_token");
  localStorage.removeItem("dms_refresh");
  localStorage.removeItem("dms_user");
  if (window.location.pathname !== "/login") {
    const sep = reason ? `?reason=${encodeURIComponent(reason)}` : "";
    window.location.href = `/login${sep}`;
  }
}

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const original = err?.config;
    const status = err?.response?.status;

    // Skip refresh attempts for the refresh / login / logout endpoints themselves
    const url = (original?.url || "");
    const isAuthEndpoint = url.includes("/auth/login") || url.includes("/auth/refresh") || url.includes("/auth/logout");

    if (status === 401 && original && !original._retried && !isAuthEndpoint) {
      original._retried = true;
      let newToken;
      try {
        if (!refreshInFlight) refreshInFlight = performRefresh().finally(() => { refreshInFlight = null; });
        newToken = await refreshInFlight;
      } catch (e) {
        if (window.location.pathname === "/login") { forceLogout("expired"); return Promise.reject(err); }
        const motivo = e?.response?.data?.detail === "Sessione scaduta per inattività" ? "inattivita" : "non_valida";
        try { newToken = await attendiRientro(motivo); } catch { return Promise.reject(err); }
      }
      original.headers = { ...(original.headers || {}), Authorization: `Bearer ${newToken}` };
      return api.request(original);
    }
    if (status === 401 && original?._retried) {
      forceLogout("expired");
    }

    // Sprint 2B.2-A bug-fix: normalize Pydantic v2 validation errors so that
    // every existing `toast.error(err.response.data.detail || "Errore")` call
    // receives a string and never crashes React with "Objects are not valid
    // as a React child".
    const data = err?.response?.data;
    if (data) {
      const d = data.detail;
      if (Array.isArray(d)) {
        data.detail = d.map((e) => {
          const field = Array.isArray(e?.loc) ? e.loc.slice(-1)[0] : "campo";
          return `${field}: ${e?.msg || "errore"}`;
        }).join(" | ");
      } else if (d && typeof d === "object" && (d.msg || d.type)) {
        const field = Array.isArray(d.loc) ? d.loc.slice(-1)[0] : "campo";
        data.detail = `${field}: ${d.msg || "errore"}`;
      }
    }

    return Promise.reject(err);
  },
);

export const getToken = () => localStorage.getItem("dms_token");

// Download documenti polimorfici (DocumentsTab → VehicleDetail/AnagraficaDetail).
// Endpoint backend mantenuto per il sistema polimorfico vivo (lo scope check
// per owner_type sarà aggiunto in PRIV-SCOPE.2).
export const downloadUrl = (docId) =>
  `${API}/documents/${docId}/download?auth=${encodeURIComponent(getToken() || "")}`;

// Sprint 2C — Foto veicolo: URL diretto per <img src> (auth in query)
export const vehicleFotoUrl = (vehicleId, photoId) =>
  `${API}/vehicles/${vehicleId}/foto/${photoId}?auth=${encodeURIComponent(getToken() || "")}`;
